// Spielerphysik — das Herzstück (Konzept §17, Prototyp 1).
//
// Alles hier dient dem Satz: „Wenn sich in fünfzehn Minuten das ‚soll ich,
// soll ich nicht' einstellt und der Moment des Aufspringens den Puls
// hochtreibt — dann trägt es."
//
// Die eine Regel, aus der die Spannung kommt: der Griff hält nur unter
// GRAB_MAX_RELATIVE_SPEED. Stehender Zug = trivial, anfahrender = Zeitfenster,
// fahrender = unmöglich. Missglückt der Griff, stolpert man — Zeit und der
// verpasste Zug sind die einzigen Kosten (Konzept §11: nicht arcadig).
//
// Mitfahren hält die Position im Waggon-Bezugssystem (CONTRACT.md), nicht als
// Weltoffset — sonst driftet man in Kurven und bei Überhöhung aus dem Waggon.

import * as THREE from 'three';
import {
  PLAYER_EYE_HEIGHT, PLAYER_HEIGHT, PLAYER_RADIUS,
  WALK_SPEED, RUN_SPEED, CROUCH_SPEED,
  GRAVITY, JUMP_SPEED, MAX_STEP_HEIGHT,
  FALL_HURT_SPEED,
  WARMTH_MAX, WETNESS_MAX, TRACK_BED_HALF_WIDTH,
} from '../../shared/constants.js';
import { projectToTrack, STATIONS, trackFrame, trackTangent } from '../../shared/track.js';
import { terrainHeight, terrainNormal, isVoidAt } from '../../shared/terrain.js';
import { ANIM } from '../../shared/protocol.js';
import { PlayerFlashlight } from './flashlight.js';
import {
  enterGodMode, exitGodMode, stepFly, isGodMode,
  FLY_SPEED, FLY_SPEED_FAST,
} from './godmode.js';
import * as climb from './climb.js';

// --- Physik-Raten (straffer Bodengriff, leicht flotter als zuvor) ------------
const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 8;

// Beschleunigung: Rennen braucht kurzen Anlauf — Entscheidung, kein Teleport.
//
// Rechnung (sichtbare Echtzeit; ACCEL_REAL=0.16 m/s², GRAB_MAX=3.4,
// RUN=6.7, WALK=2.7, ACCEL_GROUND=26, DECEL_GROUND=40):
//   • Stehend greifbar bis v_zug=3.4 → t=3.4/0.16 ≈ 21 s nach Abfahrt.
//   • Sprint-Einholen bis v_zug=RUN+GRAB_MAX=10.1 → t≈63 s, danach unmöglich.
//   • |v_p−v_z|≤3.4: Vollsprint (6.7) greift ab v_zug≳3.3 — darunter
//     ist Mitgehen/Walk sicherer. Der Sprint zählt, sobald der Zug anzieht
//     und man Strecke zum Greifpunkt schließen muss (~10–15 m Waggonweg).
//   • ACCEL_RUN_RAMP=0.55 s: 95 % RUN nach ≈0.46 s, Weg in 2 s ≈11.5 m.
//   • ACCEL_GROUND=26: 0→6.7 in ≈0.26 s sobald maxSpeed es zulässt.
//   • DECEL_GROUND=40: Stop aus Sprint ≈0.17 s — spürbarer Grip, kein Eislaufen.
const ACCEL_GROUND = 26.0;
const ACCEL_RUN_RAMP = 0.55;    // s bis runCharge=1 (siehe Rechnung oben)
const RUN_CHARGE_DECAY = 1.8;   // /s — nicht hart auf 0, sonst klebt der Sprint
const DECEL_GROUND = 40.0;
const DECEL_AIR = 1.2;
const ACCEL_AIR = 3.5;

// justPressed ist frameweise (input.endFrame). Fixed-DT-Substeps laufen aber
// nicht in jedem Frame (ab ~90 fps: häufig 0 Substeps). Ohne Latch gehen
// Sprung/Greifen in Null-Substep-Frames verloren — auf 144 Hz jeder 2.–3.
// Latch: Frame sammelt, erster Substep verbraucht; ohne Substep bleibt der
// Latch bis TTL (kein unbegrenztes Nachhängen → kein verzögertes Spät-Springen).
const EDGE_LATCH_TTL = 0.12;    // s, ~100–150 ms Verfallsfenster
// Coyote: kurzes Fenster nach Kantenverlust — Waggondach/Übersetzen sonst zickig.
const COYOTE_TIME = 0.10;       // s
// Sprungpuffer: Druck kurz vor Landung zählt bei Bodenkontakt (Standard-Feel).
const JUMP_BUFFER_TIME = 0.12;  // s

// Schotter im Bett: langsamer als fester Boden — Textur für den Bahnkorridor.
const BALLAST_SPEED_MUL = 0.72;
const SLOPE_BRAKE_START = 0.55; // Normal.y darunter bremst
const SLOPE_BRAKE_FULL = 0.35;

const CROUCH_EYE = 0.95;
const HANG_EYE = 1.35;
const DOWN_EYE = 0.35;

const STUMBLE_STUN = 0.85;      // s, nach verfehltem Griff
const DOWN_RECOVER = 4.5;       // s am Boden, bevor man aufstehen kann
const EDGE_WARN = 0.28;         // m vom Deckrand: leichte "weiter geht's"-Warnung
// Bahnsteige: Terrain kann unter Schienenoberkante liegen → großzügiger Step.
const STEP_SURFACE_PLAYER = 0.95;

const PITCH_MIN = -1.45;
const PITCH_MAX = 1.45;

// Wiederverwendete Vektoren — nichts pro Frame allokieren (CONTRACT / §14).
const _wish = new THREE.Vector3();
const _velXZ = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _tmp2 = new THREE.Vector3();
const _local = new THREE.Vector3();
const _world = new THREE.Vector3();
const _tangent = { x: 0, y: 0, z: 0 };
const _frame = {
  pos: { x: 0, y: 0, z: 0 },
  tangent: { x: 0, y: 0, z: 0 },
  right: { x: 0, y: 0, z: 0 },
  heading: 0,
};
// Letztes Kollisionsergebnis (Support-Y für Bodenauflösung) — kein Allok.
const _colHit = {
  supportY: null,
  hitSolid: false,
  hitMoving: false,
  hitCarId: null,
  tests: 0,
  pushed: false,
};

function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
function lerp(a, b, t) { return a + (b - a) * t; }
function approach(cur, target, rate, dt) {
  if (cur < target) return Math.min(target, cur + rate * dt);
  return Math.max(target, cur - rate * dt);
}

export class PlayerSystem {
  async init(ctx) {
    this.acc = 0;
    this.runCharge = 0;
    this.stunT = 0;
    this.downT = 0;
    this.edgePush = 0;
    this.joltSway = 0;
    this._anim = ANIM.IDLE;
    // Edge-Latches (Restzeit > 0 = noch gültig) + Jump-Assist
    this._jumpLatchT = 0;
    this._interactLatchT = 0;
    this._jumpNow = false;
    this._interactNow = false;
    this.coyoteT = 0;
    this.jumpBufferT = 0;

    // Lokale Waggon-Koordinaten (nur gültig wenn onCarId gesetzt).
    this.carLocal = new THREE.Vector3();
    this.carVy = 0;
    this.hangGrab = null; // { carId, x, y, z } — Kompat/Tests
    this.climbLadder = null; // { carId, x, z, y0, y1, side }
    this.climbSide = null;
    // Halten von E am Griff (0..GRAB_HOLD_TIME); verfällt außerhalb.
    this.grabHoldT = 0;
    // Mantling: Fortschritt vor Start + Restzeit im Mantle-Zustand.
    this.mantleHoldT = 0;
    this.mantleT = 0;
    this._mantleTarget = null;
    // Wiederverwendeter Trefferpuffer: Die Leiter-Suche läuft in jedem
    // Physik-Substep. Ein neues Objekt pro Treffer würde beim bloßen
    // Danebenstehen dauerhaft Garbage erzeugen.
    this._nearGrabCache = {
      carId: null,
      ladder: null,
      grabY: 0,
      worldX: 0,
      worldZ: 0,
      worldY0: 0,
      worldY1: 0,
      rel: 0,
      grab: { x: 0, y: 0, z: 0, side: null },
    };
    this._nearGrab = null;

    const pos = new THREE.Vector3();
    const vel = new THREE.Vector3();

    // Spawn: Südbahnhof, seitlich vom Gleis, nachts (Konzept §17).
    // Man wacht dort auf, ohne zu wissen wo man ist.
    const st = STATIONS[0];
    const fr = trackFrame(st.s, _frame);
    const lateral = (st.platformLateral ?? 6.2) + 2.4;
    pos.set(
      fr.pos.x + fr.right.x * lateral,
      0,
      fr.pos.z + fr.right.z * lateral,
    );
    const hint = st.s;
    pos.y = terrainHeight(pos.x, pos.z, hint);

    this.state = {
      pos,
      velocity: vel,
      yaw: Math.atan2(-fr.right.x, -fr.right.z), // Blick zum Gleis
      pitch: 0,
      mode: 'ground',
      onCarId: null,
      anim: ANIM.IDLE,
      warmth: 78,
      wetness: 12,
      eyeHeight: PLAYER_EYE_HEIGHT,
      crouching: false,
      grounded: true,
      trackS: st.s,
      lateral,
      // HUD-Kontext (über Vertrag hinaus, kein Konflikt):
      canGrab: false,
      grabCarId: null,
      prompt: null,
      edgeWarn: false,
      comfort: 1, // Kamera/VR: Amplituden-Skalierung
      // Dev/Hilfsmittel — nicht Teil der Kernphysik, aber test- und debugbar.
      flashlightOn: false,
    };

    // Taschenlampe an der Szene (Standard aus). Null im Headless-Test ohne scene.
    this.flashlight = ctx.scene
      ? new PlayerFlashlight(ctx.scene, ctx.THREE ?? THREE)
      : null;

    this._targetEye = PLAYER_EYE_HEIGHT;
    this._publish(ctx);
    ctx.trackHint = st.s;
  }

  update(dt, ctx) {
    if (!ctx.started && ctx.started !== undefined) {
      // Vor dem Klick: Zustand trotzdem publizieren, Physik pausiert.
      this._publish(ctx);
      return;
    }

    const input = ctx.input;
    if (input) {
      const look = input.takeLook();
      // Maus rechts → yaw+, Blick nach rechts (Kamera: yaw0=-Z, +yaw→+X).
      this.state.yaw += look.dx;
      this.state.pitch = clamp(this.state.pitch - look.dy, PITCH_MIN, PITCH_MAX);

      // Godmode (Dev): eine Taste hin und zurück — Strecke ist 7,5 km.
      if (input.justPressed('godMode')) {
        if (isGodMode(this.state)) this._exitGodMode(ctx);
        else this._enterGodMode(ctx);
      }
      // Taschenlampe: eigene Taste, Standard aus (Nacht spielbar halten).
      if (input.justPressed('flashlight')) {
        this.state.flashlightOn = this.flashlight
          ? this.flashlight.toggle()
          : !this.state.flashlightOn;
      }
    }

    // justPressed → Latch (Frame-Zeit). Vor den Substeps, damit Null-Substep-
    // Frames die Kante nicht verwerfen (siehe EDGE_LATCH_TTL).
    // Im Godmode kein Greifen/Springen-Latch — Flug nutzt down(), nicht Kanten.
    if (!isGodMode(this.state)) this._collectEdges(input, dt);

    // Fixed-Timestep: bei 30 und 144 fps identische Physik — entscheidend,
    // weil ein 20-Sekunden-Zeitfenster über Erfolg entscheidet.
    this.acc += Math.min(dt, 0.1);
    let steps = 0;
    while (this.acc >= FIXED_DT && steps < MAX_SUBSTEPS) {
      // Kanten nur im ersten Substep dieses Frames materialisieren —
      // sonst Doppelsprung bei 30 fps (2 Substeps / Frame).
      this._armEdgesForStep(steps === 0);
      this._step(FIXED_DT, ctx);
      this.acc -= FIXED_DT;
      steps++;
    }
    if (this.acc >= FIXED_DT) this.acc = 0; // Spiral-of-Death abschneiden

    // Weiche Kopfhöhe (visuell, nicht Physik)
    this.state.eyeHeight = lerp(this.state.eyeHeight, this._targetEye, 1 - Math.exp(-12 * dt));

    // Licht an Blick koppeln — auch wenn aus (Position bleibt aktuell fürs Anschalten).
    if (this.flashlight) {
      this.state.flashlightOn = this.flashlight.on;
      this.flashlight.sync(this.state);
    }

    this._publish(ctx);
    this._sendNet(ctx);
  }

  /** Frame-justPressed einsammeln; TTL in Echtzeit, nicht in Fixed-DT. */
  _collectEdges(input, frameDt) {
    if (this._jumpLatchT > 0) this._jumpLatchT = Math.max(0, this._jumpLatchT - frameDt);
    if (this._interactLatchT > 0) this._interactLatchT = Math.max(0, this._interactLatchT - frameDt);
    if (!input) return;
    // Frischer Druck setzt TTL neu (erneutes Drücken verlängert nicht „alt“,
    // sondern startet sauber — ein Tastendruck = ein Impuls).
    if (input.justPressed('jump')) this._jumpLatchT = EDGE_LATCH_TTL;
    if (input.justPressed('interact')) this._interactLatchT = EDGE_LATCH_TTL;
  }

  /**
   * Erster Substep: Latch → _jumpNow/_interactNow und verbrauchen.
   * Weitere Substeps / keine Substeps: Kanten bleiben gelatcht bzw. aus.
   */
  _armEdgesForStep(isFirstSubstep) {
    this._jumpNow = false;
    this._interactNow = false;
    if (!isFirstSubstep) return;
    if (this._jumpLatchT > 0) {
      this._jumpNow = true;
      this._jumpLatchT = 0;
    }
    if (this._interactLatchT > 0) {
      this._interactNow = true;
      this._interactLatchT = 0;
    }
  }

  // -------------------------------------------------------------------------
  // Ein Physik-Substep
  // -------------------------------------------------------------------------

  _step(dt, ctx) {
    const st = this.state;
    const input = ctx.input;
    const train = ctx.train;

    if (this.stunT > 0) this.stunT = Math.max(0, this.stunT - dt);
    if (this.downT > 0) this.downT = Math.max(0, this.downT - dt);

    st.canGrab = false;
    st.grabCarId = null;
    st.prompt = null;
    st.edgeWarn = false;

    // --- Godmode: keine Gravitation, keine Kollision, keine Kälte ----------
    // mode === 'fly' ist der stabile Hook für künftige Boden-Kollision.
    if (st.mode === 'fly') {
      stepFly(this, dt, ctx);
      const projFly = projectToTrack(st.pos.x, st.pos.z, ctx.trackHint ?? st.trackS);
      st.trackS = projFly.s;
      st.lateral = projFly.lateral;
      ctx.trackHint = projFly.s;
      // Kälte/Nässe eingefroren — Entwickler soll die Strecke prüfen, nicht erfrieren.
      this._updateAnim(ctx);
      this._targetEye = PLAYER_EYE_HEIGHT;
      return;
    }

    // Ducken nur Control — KeyC teilt input.js mit photo.js (Foto).
    // Bloßes C = knipsen (photo.js); sonst crouch+photo gleichzeitig und unbenutzbar.
    // Fallback down('crouch') für Test-Mocks ohne key().
    const wantCrouch = !!(input && st.mode !== 'down' && st.mode !== 'hang' && st.mode !== 'mantle' && st.mode !== 'climb' && (
      typeof input.key === 'function'
        ? (input.key('ControlLeft') || input.key('ControlRight'))
        : input.down('crouch')
    ));
    st.crouching = wantCrouch && (st.mode === 'ground' || st.mode === 'ride' || st.mode === 'inside' || st.mode === 'air');

    switch (st.mode) {
      case 'down':
        this._stepDown(dt, ctx);
        break;
      case 'hang':
        this._stepHang(dt, ctx);
        break;
      case 'climb':
        this._stepClimb(dt, ctx);
        break;
      case 'mantle':
        this._stepMantle(dt, ctx);
        break;
      case 'ride':
      case 'inside':
        this._stepRide(dt, ctx);
        break;
      default:
        this._stepGroundAir(dt, ctx);
        break;
    }

    // Projektion für alle anderen Systeme (Performance-Hinweis).
    const proj = projectToTrack(st.pos.x, st.pos.z, ctx.trackHint ?? st.trackS);
    st.trackS = proj.s;
    st.lateral = proj.lateral;
    ctx.trackHint = proj.s;

    this._updateWarmth(dt, ctx);
    this._updateAnim(ctx);

    // Ziel-Kopfhöhe
    if (st.mode === 'down') this._targetEye = DOWN_EYE;
    else if (st.mode === 'hang' || st.mode === 'mantle') this._targetEye = HANG_EYE;
    else if (st.mode === 'climb') this._targetEye = HANG_EYE;
    else if (st.crouching) this._targetEye = CROUCH_EYE;
    else this._targetEye = PLAYER_EYE_HEIGHT;
  }

  // --- Godmode (Dev-Werkzeug) — Logik in godmode.js -----------------------

  _enterGodMode(ctx) {
    enterGodMode(this);
    if (ctx.log) ctx.log('godmode an');
  }

  _exitGodMode(ctx) {
    exitGodMode(this, ctx);
    if (ctx.log) ctx.log('godmode aus');
  }

  // --- Boden / Luft ---------------------------------------------------------

  _stepGroundAir(dt, ctx) {
    const st = this.state;
    const input = ctx.input;
    const train = ctx.train;
    const stunned = this.stunT > 0;

    // Wunschrichtung aus Blick + WASD
    const axis = input && !stunned ? input.moveAxis() : { x: 0, y: 0 };
    // forward = (sin y, 0, -cos y), right = (cos y, 0, sin y) — wie Kamera
    const sy = Math.sin(st.yaw), cy = Math.cos(st.yaw);
    _wish.set(
      axis.x * cy + axis.y * sy,
      0,
      axis.x * sy - axis.y * cy,
    );
    if (_wish.lengthSq() > 1e-8) _wish.normalize();

    const wantRun = !!(input && input.down('run') && !st.crouching && axis.y > 0.1);
    if (wantRun && _wish.lengthSq() > 0.01 && st.grounded) {
      this.runCharge = Math.min(1, this.runCharge + dt / ACCEL_RUN_RAMP);
    } else {
      // Sanfter Abfall: hartes Nullen fühlt sich klebrig/abrupt an.
      this.runCharge = Math.max(0, this.runCharge - dt * RUN_CHARGE_DECAY);
    }

    let maxSpeed = st.crouching ? CROUCH_SPEED : WALK_SPEED;
    if (!st.crouching && this.runCharge > 0) {
      // Smoothstep bleibt: frühe Charge nicht linear voll — Entscheidung spürbar,
      // aber mit 0.75 s Rampe nicht mehr träge (siehe Kommentar an ACCEL_RUN_RAMP).
      const t = this.runCharge;
      maxSpeed = lerp(WALK_SPEED, RUN_SPEED, t * t * (3 - 2 * t));
    }

    // Schotter im Gleisbett
    const proj = projectToTrack(st.pos.x, st.pos.z, st.trackS);
    if (Math.abs(proj.lateral) <= TRACK_BED_HALF_WIDTH + 0.4) {
      maxSpeed *= BALLAST_SPEED_MUL;
    }

    // Steile Hänge bremsen (Normalen-Y)
    if (st.grounded) {
      const n = terrainNormal(st.pos.x, st.pos.z, proj.s);
      if (n.y < SLOPE_BRAKE_START) {
        const t = clamp((SLOPE_BRAKE_START - n.y) / (SLOPE_BRAKE_START - SLOPE_BRAKE_FULL), 0, 1);
        maxSpeed *= 1 - t * 0.75;
      }
    }

    // Kälte verlangsamt (keine Todesstrafe, nur Kosten)
    if (st.warmth < 25) {
      maxSpeed *= lerp(0.55, 1, st.warmth / 25);
    }

    // Horizontal beschleunigen / bremsen
    _velXZ.set(st.velocity.x, 0, st.velocity.z);
    if (_wish.lengthSq() > 0.01 && !stunned) {
      const accel = st.grounded ? ACCEL_GROUND : ACCEL_AIR;
      if (st.grounded) {
        // Quer- und Gegenkomponente abbauen — sonst gleitet man wie auf Eis,
        // weil reines Addieren die alte Richtung zu lange mitschleppt.
        const along = _velXZ.x * _wish.x + _velXZ.z * _wish.z;
        let keepAlong = along;
        if (along < 0) {
          keepAlong = Math.min(0, along + DECEL_GROUND * dt);
          if (keepAlong > -1e-5) keepAlong = 0;
        }
        let lx = _velXZ.x - _wish.x * along;
        let lz = _velXZ.z - _wish.z * along;
        const latSp = Math.hypot(lx, lz);
        if (latSp > 1e-5) {
          const ns = Math.max(0, latSp - DECEL_GROUND * dt);
          if (ns <= 1e-5) { lx = 0; lz = 0; }
          else { lx *= ns / latSp; lz *= ns / latSp; }
        }
        _velXZ.x = _wish.x * keepAlong + lx;
        _velXZ.z = _wish.z * keepAlong + lz;
      }
      _velXZ.x += _wish.x * accel * dt;
      _velXZ.z += _wish.z * accel * dt;
      const sp = _velXZ.length();
      if (sp > maxSpeed) _velXZ.multiplyScalar(maxSpeed / sp);
    } else {
      const decel = st.grounded ? DECEL_GROUND : DECEL_AIR;
      const sp = _velXZ.length();
      if (sp > 0) {
        const ns = Math.max(0, sp - decel * dt);
        if (ns <= 1e-5) _velXZ.set(0, 0, 0);
        else _velXZ.multiplyScalar(ns / sp);
      }
    }
    st.velocity.x = _velXZ.x;
    st.velocity.z = _velXZ.z;

    // Sprung: Latch (frame-sicher) + Coyote + Puffer. Coyote/Puffer verändern
    // die Flugphysik nicht — sie machen nur die Eingabe fair auf Kanten/Landung.
    if (this.jumpBufferT > 0) this.jumpBufferT = Math.max(0, this.jumpBufferT - dt);

    if (!stunned && !st.crouching) {
      if (this._jumpNow) {
        if (st.grounded || this.coyoteT > 0) {
          this._applyGroundJump();
        } else {
          // In der Luft gedrückt: bei Landung nachholen.
          this.jumpBufferT = JUMP_BUFFER_TIME;
        }
      }
    }

    // Gravitation
    if (!st.grounded) {
      st.velocity.y -= GRAVITY * dt;
    }

    // Integration
    st.pos.x += st.velocity.x * dt;
    st.pos.y += st.velocity.y * dt;
    st.pos.z += st.velocity.z * dt;

    // Welthindernisse (Mauer, Bahnsteig, Waggon-Außenhülle) — in Substeps,
    // damit 30 und 144 fps denselben Kontakt liefern.
    this._resolveWorldCollision(ctx);

    this._resolveGround(ctx);

    // Coyote nach Bodenauflösung: am Boden voll, sonst herunterzählen.
    if (st.grounded) this.coyoteT = COYOTE_TIME;
    else this.coyoteT = Math.max(0, this.coyoteT - dt);

    // Sprungpuffer: kurz vor Landung gedrückt → jetzt abspringen.
    if (st.grounded && this.jumpBufferT > 0 && !stunned && !st.crouching) {
      this._applyGroundJump();
      this.jumpBufferT = 0;
    }

    // Aufspringen: Leiter in Reichweite suchen, E halten (nicht tippen).
    // Latch bleibt für Jump/andere Aktionen; Griff braucht continuous down.
    if (train && st.mode !== 'down' && this.stunT <= 0) {
      this._tryGrabProximity(ctx);
      if (climb.updateGrabHold(this, dt, ctx)) {
        this._attemptGrab(ctx);
      }
    } else {
      this.grabHoldT = Math.max(0, this.grabHoldT - dt * 2.6);
    }

    // Mantling an Kanten (Bahnsteig, Mauer, niedriges Deck).
    // Leiter in Reichweite hat Vorrang — Mantle-Halten ist kürzer und würde
    // sonst den Griff stehlen (mode=mantle statt climb).
    if ((st.mode === 'ground' || st.mode === 'air') && this.stunT <= 0 && !st.canGrab) {
      climb.tryMantle(this, dt, ctx);
    } else if (st.canGrab) {
      this.mantleHoldT = 0;
    }

    // Sehr tiefe Fälle: Brücke/Abgrund — ehrlich, nicht arcadig. Respawn am Hof.
    if (st.pos.y < proj.trackY - 80) {
      this._respawnYard(ctx);
    }
  }

  /**
   * Statik + Waggon-Außenhülle. Ride/hang/climb bleiben im Waggon-Bezug —
   * dort würde Außenkollision das mühsam driftfreie Mitfahren zerlegen.
   */
  _resolveWorldCollision(ctx) {
    const st = this.state;
    _colHit.supportY = null;
    _colHit.hitSolid = false;
    _colHit.hitMoving = false;
    _colHit.hitCarId = null;
    _colHit.tests = 0;
    _colHit.pushed = false;

    // Godmode fliegt durch alles (CONTRACT: mode fly skippen).
    if (st.mode === 'ride' || st.mode === 'inside' || st.mode === 'hang'
      || st.mode === 'climb' || st.mode === 'mantle' || st.mode === 'fly') {
      return;
    }

    const col = ctx.collision ?? ctx.systems?.collision;
    if (!col?.resolvePlayer) return;

    const trains = ctx.trains ?? (ctx.train ? [ctx.train] : []);
    const bodyH = st.crouching ? CROUCH_EYE + 0.15 : PLAYER_HEIGHT;
    const res = col.resolvePlayer(st.pos, st.velocity, {
      radius: PLAYER_RADIUS,
      bodyHeight: bodyH,
      maxStep: MAX_STEP_HEIGHT,
      trains,
      workers: true,
    });

    _colHit.supportY = res.supportY;
    _colHit.hitSolid = res.hitSolid;
    _colHit.hitMoving = res.hitMoving;
    _colHit.hitCarId = res.hitCarId;
    _colHit.tests = res.tests;
    _colHit.pushed = res.pushed;

    // Fahrender Zug erwischt: stolpern/stun (Konzept §11, nicht arcadig).
    if (res.hitMoving && this.stunT <= 0 && st.mode !== 'down') {
      this.stunT = STUMBLE_STUN * 0.7;
      this.runCharge = 0;
      // Seitlich weg vom Zug, falls laterale Info da
      if (Math.abs(st.lateral) > 0.1) {
        const side = Math.sign(st.lateral);
        trackTangent(st.trackS, _tangent);
        st.velocity.x += _tangent.z * side * 2.8;
        st.velocity.z += -_tangent.x * side * 2.8;
      }
      if (ctx.log) ctx.log('der zug streift dich');
    }
  }

  _resolveGround(ctx) {
    const st = this.state;
    const proj = projectToTrack(st.pos.x, st.pos.z, st.trackS);

    // Über Brücke neben dem Gleis: kein Boden (Konzept §11: Fehltritt final).
    // Bahnsteig-Support darf void nicht „überbrücken" — void bleibt final.
    if (isVoidAt(proj.s, proj.lateral)) {
      st.grounded = false;
      st.mode = 'air';
      return;
    }

    let groundY = terrainHeight(st.pos.x, st.pos.z, proj.s);
    // Begehbare Oberflächen (Bahnsteig) liegen über dem Terrain.
    const solidY = _colHit.supportY;
    // Step-Limit: nur annehmen, wenn man die Kante erreichen kann oder schon drauf ist.
    if (solidY != null && solidY > groundY) {
      const rise = solidY - st.pos.y;
      if (rise <= STEP_SURFACE_PLAYER || st.pos.y >= solidY - 0.12) {
        groundY = solidY;
      }
    }

    const feet = st.pos.y;
    const vy = st.velocity.y;
    // Bahnsteig: stepH größer als Terrain-MAX_STEP, sonst steckt man vor der Kante.
    const onSolid = solidY != null && groundY === solidY;
    const stepH = onSolid
      ? Math.max(MAX_STEP_HEIGHT, STEP_SURFACE_PLAYER)
      : MAX_STEP_HEIGHT;

    if (vy <= 0 && feet <= groundY + 0.08) {
      const below = groundY - feet; // >0 wenn unter der Fläche
      if (below >= -0.08 && below <= stepH) {
        // Landung oder Step-up (Bahnsteig/Kiste).
        if (!st.grounded && -vy >= FALL_HURT_SPEED && below < 0.2) {
          // Harte Landung nur aus der Luft aus geringer Resthöhe
          this._enterDown();
        }
        st.pos.y = groundY;
        st.velocity.y = 0;
        st.grounded = true;
        if (st.mode !== 'down') st.mode = 'ground';
      } else if (below > stepH && feet < groundY - 0.02) {
        // Zu tief unter einer hohen Kante — nicht teleportieren; fallen/Terrain.
        // (Terrain-Fall: groundY ist Terrain, below sollte klein sein.)
        if (!onSolid) {
          st.pos.y = groundY;
          st.velocity.y = 0;
          st.grounded = true;
          if (st.mode === 'air') st.mode = 'ground';
        }
      }
    } else if (feet > groundY + 0.08) {
      st.grounded = false;
      if (st.mode === 'ground') st.mode = 'air';
    } else if (feet < groundY - 0.02 && vy <= 0) {
      st.pos.y = groundY;
      st.velocity.y = 0;
      st.grounded = true;
      if (st.mode === 'air') st.mode = 'ground';
    }
  }

  /** Boden-/Coyote-Sprung. Ein Aufruf = genau ein Impuls (Latch schon verbraucht). */
  _applyGroundJump() {
    const st = this.state;
    st.velocity.y = JUMP_SPEED;
    st.grounded = false;
    st.mode = 'air';
    this.coyoteT = 0;
    this.jumpBufferT = 0;
  }

  // --- Greifen / Aufspringen / Klettern (Logik in climb.js) -----------------

  _tryGrabProximity(ctx) {
    climb.tryGrabProximity(this, ctx);
  }

  _relativeSpeed(ctx, car) {
    return climb.relativeSpeed(this, ctx, car);
  }

  _attemptGrab(ctx) {
    // Produktionspfad: Leiter → climb. Tests ohne ladder: Hang-Kompat.
    climb.attemptGrabCompat(this, ctx);
  }

  _stumble(ctx, car) {
    climb.stumble(this, ctx, car);
    this.stunT = STUMBLE_STUN;
  }

  _stepHang(dt, ctx) {
    climb.stepHang(this, dt, ctx);
  }

  _stepClimb(dt, ctx) {
    climb.stepClimb(this, dt, ctx);
  }

  _stepMantle(dt, ctx) {
    climb.stepMantle(this, dt, ctx);
  }

  // --- Mitfahren (Waggon-Bezugssystem) --------------------------------------

  _stepRide(dt, ctx) {
    const st = this.state;
    const train = ctx.train;
    const input = ctx.input;

    if (!train || !st.onCarId) {
      this._detachToAir(ctx);
      return;
    }

    const car = train.carById?.(st.onCarId);
    if (!car) {
      this._detachToAir(ctx);
      return;
    }

    // Jolt: stehend Gleichgewicht verlieren, sitzend (ducken) nicht —
    // stille Belohnung ohne UI (Konzept).
    const jolt = train.jolt ?? 0;
    if (!st.crouching && jolt > 0.55 && st.grounded) {
      this.joltSway += jolt * dt;
      if (this.joltSway > 0.35) {
        this.carLocal.x += (Math.random() - 0.5) * 0.04 * jolt;
        this.carLocal.z += (Math.random() - 0.5) * 0.02 * jolt;
        this.joltSway = 0;
      }
    } else {
      this.joltSway = Math.max(0, this.joltSway - dt);
    }

    const axis = input && this.stunT <= 0 ? input.moveAxis() : { x: 0, y: 0 };
    const sy = Math.sin(st.yaw), cy = Math.cos(st.yaw);

    // Bewegung in Welt-XZ, dann zurück nach lokal — so bleibt yaw absolut
    // und der Spieler steuert wie am Boden. Vorwärts = Kamerablick (yaw0=-Z).
    let maxSpeed = st.crouching ? CROUCH_SPEED : WALK_SPEED * 0.9;
    if (input && input.down('run') && !st.crouching) {
      this.runCharge = Math.min(1, this.runCharge + dt / ACCEL_RUN_RAMP);
      maxSpeed = lerp(WALK_SPEED * 0.9, RUN_SPEED * 0.75, this.runCharge);
    } else {
      this.runCharge = Math.max(0, this.runCharge - dt * RUN_CHARGE_DECAY);
    }
    if (st.warmth < 25) maxSpeed *= lerp(0.55, 1, st.warmth / 25);

    _wish.set(
      axis.x * cy + axis.y * sy,
      0,
      axis.x * sy - axis.y * cy,
    );

    // Aktuelle Weltposition aus lokal (Bezugssystem!)
    this._syncWorldFromLocal(ctx);

    if (_wish.lengthSq() > 0.01) {
      _wish.normalize().multiplyScalar(maxSpeed * dt);
      _world.set(st.pos.x + _wish.x, st.pos.y, st.pos.z + _wish.z);
      if (train.worldToLocal) {
        train.worldToLocal(st.onCarId, _world, _local);
        this.carLocal.x = _local.x;
        this.carLocal.z = _local.z;
      } else {
        this.carLocal.x += _wish.x;
        this.carLocal.z += _wish.z;
      }
    }

    // Vertikal auf dem Deck (Latch: gleiches High-FPS-Problem wie am Boden)
    if (st.grounded && this._jumpNow && !st.crouching) {
      this.carVy = JUMP_SPEED * 0.85;
      st.grounded = false;
    }
    if (!st.grounded) {
      this.carVy -= GRAVITY * dt;
      this.carLocal.y += this.carVy * dt;
    }

    // Decks: harte Ränder — wer drüber läuft, fällt (bei 50 km/h das Ende).
    const decks = car.decks ?? [];
    const support = this._findDeck(decks, this.carLocal.x, this.carLocal.z, this.carLocal.y);
    if (support) {
      const d = support.deck;
      const marginX = Math.min(this.carLocal.x - d.minX, d.maxX - this.carLocal.x);
      const marginZ = Math.min(this.carLocal.z - d.minZ, d.maxZ - this.carLocal.z);
      const margin = Math.min(marginX, marginZ);
      // Kleine ehrliche Warnung am Rand, keine unsichtbare Wand
      st.edgeWarn = margin < EDGE_WARN;
      if (st.edgeWarn) this.edgePush = (EDGE_WARN - margin) / EDGE_WARN;
      else this.edgePush = 0;

      if (!st.grounded && this.carVy <= 0 && this.carLocal.y <= d.y + 0.08) {
        this.carLocal.y = d.y;
        this.carVy = 0;
        st.grounded = true;
      } else if (st.grounded) {
        this.carLocal.y = d.y;
        this.carVy = 0;
      }
      st.mode = d.kind === 'inside' ? 'inside' : 'ride';
    } else {
      // Kein Deck unter den Füßen
      // Zwischen Waggons? (Konzept §11: Ausrutschen möglich und riskant)
      const transferred = this._tryCarTransfer(ctx, car);
      if (!transferred) {
        // Noch in der Nähe eines Decks in XZ, aber y zu hoch/niedrig?
        const near = this._nearestDeckXZ(decks, this.carLocal.x, this.carLocal.z);
        if (near && near.dist < 0.35 && st.grounded) {
          // Gerade über den Rand: fallen lassen
          st.grounded = false;
          this.carVy = Math.min(this.carVy, -0.5);
        } else if (!near || near.dist > 0.6) {
          this._detachToAir(ctx);
          return;
        } else if (!st.grounded) {
          // In der Luft über dem Waggon
          if (this.carLocal.y < -1.5) {
            this._detachToAir(ctx);
            return;
          }
        }
      }
    }

    // Innenraum betreten/verlassen über Tür
    if (car.type?.ride === 'inside' || car.typeId === 'boxcar' || car.typeId === 'gondola') {
      this._doorLogic(ctx, car, input);
    }

    // Loslösen mit Ducken am Rand + Interakt? eher springen vom Zug
    if (this._interactNow && st.edgeWarn) {
      // bewusster Absprung
      this._detachToAir(ctx, true);
      return;
    }

    this._syncWorldFromLocal(ctx);

    // Weltgeschwindigkeit ≈ Zuggeschwindigkeit (für Relativ-Berechnungen / Netz)
    const real = train.realSpeed ?? 0;
    trackTangent(car.s ?? train.headS ?? 0, _tangent);
    const dir = train.direction ?? 1;
    st.velocity.set(
      _tangent.x * real * dir + (axis.x * cy + axis.y * sy) * maxSpeed,
      this.carVy,
      _tangent.z * real * dir + (-axis.x * sy + axis.y * cy) * maxSpeed,
    );
  }

  _findDeck(decks, x, z, y) {
    let best = null;
    for (const d of decks) {
      if (x < d.minX || x > d.maxX || z < d.minZ || z > d.maxZ) continue;
      const dy = y - d.y;
      // Stehen auf Deck oder kurz darüber/darunter
      if (dy >= -0.35 && dy < 1.2) {
        if (!best || Math.abs(dy) < Math.abs(best.dy)) best = { deck: d, dy };
      }
    }
    return best;
  }

  _nearestDeckXZ(decks, x, z) {
    let best = null;
    for (const d of decks) {
      const cx = clamp(x, d.minX, d.maxX);
      const cz = clamp(z, d.minZ, d.maxZ);
      const dist = Math.hypot(x - cx, z - cz);
      if (!best || dist < best.dist) best = { deck: d, dist };
    }
    return best;
  }

  /**
   * Übergang zwischen Waggons an den Deck-Enden.
   * Möglich und riskant — nicht unmöglich (Konzept §11).
   */
  _tryCarTransfer(ctx, car) {
    const train = ctx.train;
    if (!train?.cars?.length) return false;

    const idx = car.index;
    const candidates = [];
    if (idx > 0) candidates.push(train.cars[idx - 1]);
    if (idx < train.cars.length - 1) candidates.push(train.cars[idx + 1]);

    // Weltposition der Füße
    this._syncWorldFromLocal(ctx);
    const feet = this.state.pos;

    for (const other of candidates) {
      if (!other || !train.worldToLocal) continue;
      train.worldToLocal(other.id, feet, _local);
      const support = this._findDeck(other.decks ?? [], _local.x, _local.z, _local.y);
      if (support && Math.abs(support.dy) < 0.5) {
        // Riskanter Sprung: kleine Chance zu rutschen wenn nicht geduckt und jolt
        const jolt = train.jolt ?? 0;
        if (!this.state.crouching && jolt > 0.7 && Math.random() < 0.15 * jolt) {
          // Ausrutschen zwischen Waggons
          this._detachToAir(ctx);
          if (ctx.log) ctx.log('du rutschst ab');
          return true;
        }
        this.state.onCarId = other.id;
        this.carLocal.copy(_local);
        this.carLocal.y = support.deck.y;
        this.state.mode = support.deck.kind === 'inside' ? 'inside' : 'ride';
        this.state.grounded = true;
        this.carVy = 0;
        return true;
      }
    }
    return false;
  }

  _doorLogic(ctx, car, input) {
    // Durch Türöffnung: mode inside ↔ ride wenn doorOpen und seitlich an der Tür
    if (!car.doorOpen && car.typeId === 'boxcar') return;
    const interior = car.type?.interior;
    if (!interior) {
      // Gondola: oben offen, „inside" sobald im Kasten
      return;
    }
    const halfW = (interior.width ?? 2.7) / 2;
    const halfL = (interior.length ?? 14) / 2;
    const insideXZ =
      Math.abs(this.carLocal.x) <= halfW + 0.1 &&
      Math.abs(this.carLocal.z) <= halfL + 0.1;

    if (this.state.mode === 'ride' && insideXZ && this.carLocal.y <= (interior.floorY ?? car.type.deckHeight) + 0.3) {
      // Nähe Tür (x am Rand)
      const atDoor = Math.abs(Math.abs(this.carLocal.x) - halfW) < 0.45 || car.typeId === 'gondola';
      if (atDoor || car.typeId === 'gondola') {
        this.state.mode = 'inside';
        this.carLocal.y = interior.floorY ?? this.carLocal.y;
      }
    } else if (this.state.mode === 'inside' && !insideXZ) {
      this.state.mode = 'ride';
    }
  }

  // --- Down / Erholen -------------------------------------------------------

  _stepDown(dt, ctx) {
    const st = this.state;
    st.velocity.x = approach(st.velocity.x, 0, DECEL_GROUND, dt);
    st.velocity.z = approach(st.velocity.z, 0, DECEL_GROUND, dt);
    st.velocity.y -= GRAVITY * dt;
    st.pos.x += st.velocity.x * dt;
    st.pos.y += st.velocity.y * dt;
    st.pos.z += st.velocity.z * dt;
    this._resolveWorldCollision(ctx);
    this._resolveGround(ctx);
    st.mode = 'down';

    // Sehr niedrige warmth → länger am Boden
    const need = st.warmth < 10 ? DOWN_RECOVER * 1.5 : DOWN_RECOVER;
    if (this.downT <= 0) this.downT = 0;
    // downT zählt runter in _step; beim Enter setzen wir ihn
    if (this.downT <= 0 && st.warmth >= 8) {
      // Aufstehen mit springen/interact (Latch) oder Vorwärts halten
      const input = ctx.input;
      if (this._jumpNow || this._interactNow || (input && input.down('forward'))) {
        st.mode = st.grounded ? 'ground' : 'air';
        st.prompt = null;
      }
    } else {
      st.prompt = 'erholen …';
    }

    // Langsame Erholung der Wärme im Liegen (etwas)
    st.warmth = Math.min(WARMTH_MAX, st.warmth + 1.2 * dt);
  }

  _enterDown() {
    this.state.mode = 'down';
    this.state.crouching = true;
    this.downT = DOWN_RECOVER;
    this.state.velocity.x *= 0.3;
    this.state.velocity.z *= 0.3;
    this.runCharge = 0;
  }

  _detachToAir(ctx, intentional = false) {
    climb.detachToAir(this, ctx, intentional);
  }

  _syncWorldFromLocal(ctx) {
    climb.syncWorldFromLocal(this, ctx);
  }

  _respawnYard(ctx) {
    const st = this.state;
    const station = STATIONS[0];
    const fr = trackFrame(station.s, _frame);
    const lateral = (station.platformLateral ?? 6.2) + 2.4;
    st.pos.set(
      fr.pos.x + fr.right.x * lateral,
      0,
      fr.pos.z + fr.right.z * lateral,
    );
    st.pos.y = terrainHeight(st.pos.x, st.pos.z, station.s);
    st.velocity.set(0, 0, 0);
    st.mode = 'ground';
    st.onCarId = null;
    st.grounded = true;
    st.warmth = Math.max(st.warmth, 40);
    this.hangGrab = null;
    if (ctx.log) ctx.log('du kommst wieder zu dir');
  }

  // --- Kälte / Nässe (Konzept §11.1: einzige Survival-Achse) -----------------
  //
  // Kalibrierung (Konzept §2.1): Minuten zählen, nicht Sekunden.
  // Bei 49 km/h, Regen, Nacht (warmth0=80, wet0=40):
  //   Flachwagen  → Kollaps ~9–10 min (Fahrt ~11 min: Paarung ist falsch)
  //   Gondola     → überlebt die Fahrt kalt (Mittelweg, openTop)
  //   Boxcar zu   → wärmt sich, Regen egal
  //   Flachwagen trocken nachts → überlebt die Fahrt, endet kalt
  // Raten bewusst niedrig: alter Stand kollabierte in <1 min und machte die
  // Kernentscheidung („welcher Waggon?") zur reinen Binärwahl todsicher/sofort tot.

  _updateWarmth(dt, ctx) {
    const st = this.state;
    const weather = ctx.weather ?? { rain: 0, fog: 0, wind: 0 };
    const sun = ctx.sun ?? { elevation: -0.5 };
    const train = ctx.train;

    // Exposition + ob der Innenraum wirklich geschlossen ist.
    // openTop (Gondola): mode 'inside' schützt vor Wind, nicht vor Regen.
    let exposure = 1.0;
    let sealedInside = false;
    let openTopInside = false;
    const car = (st.onCarId && train?.carById) ? train.carById(st.onCarId) : null;

    if (st.mode === 'inside') {
      openTopInside = !!car?.type?.interior?.openTop;
      if (openTopInside) {
        // Offener Kasten: type.exposure (0.6) — Regen kommt an, Wind weniger.
        exposure = car?.type?.exposure ?? 0.6;
      } else {
        // Geschlossener Waggon: fast windstill und trocken.
        exposure = 0.05;
        sealedInside = true;
      }
    } else if (car) {
      exposure = car?.type?.exposure ?? 0.7;
      if (st.mode === 'hang' || st.mode === 'climb') exposure = Math.max(exposure, 0.85);
    } else if (st.mode === 'ground' || st.mode === 'air' || st.mode === 'down') {
      exposure = 0.7;
    }

    // Nässe: ~volle Nässe in wenigen Minuten auf dem Flachwagen, nicht in 20 s.
    const rain = weather.rain ?? 0;
    if (rain > 0.05) {
      const wetRate = rain * exposure * 0.30;
      st.wetness = Math.min(WETNESS_MAX, st.wetness + wetRate * dt);
    } else {
      // Trocknen: im geschlossenen Waggon spürbar, draußen langsam.
      const dryBase = sealedInside ? 2.5 : 0.7;
      const dry = dryBase * (1 - exposure * 0.5);
      st.wetness = Math.max(0, st.wetness - dry * dt);
    }

    // Fahrtwind: skaliert mit realSpeed und exposure
    const realSpeed = train?.realSpeed ?? 0;
    const windFromSpeed = clamp(realSpeed / 25, 0, 1.5); // ~90 km/h → stark
    const ambientWind = weather.wind ?? 0;
    const wind = (windFromSpeed + ambientWind * 0.3) * exposure;

    // Verdunstungskälte: nass + Wind ist der eigentliche Killer
    const wetFactor = st.wetness / WETNESS_MAX;
    const evaporative = wetFactor * wind * 0.12;

    // Nachtkälte
    const elev = sun.elevation ?? 0;
    const night = elev < 0 ? (-elev) * 0.065 * (0.4 + exposure * 0.6) : 0;

    // Grundverlust durch Fahrtwind (trocken)
    const windChill = wind * 0.05;

    // Wärmegewinne: nur der *geschlossene* Innenraum wärmt wirklich.
    // openTop bekommt keinen Inside-Bonus (Konzept: Gondola = Mittelweg).
    let gain = 0;
    if (sealedInside) gain += 0.85;
    if (st.mode === 'down') gain += 0.25;
    if (elev > 0.15) gain += elev * 0.40 * (0.3 + (1 - Math.min(exposure, 1)) * 0.4);
    // Stillstand: bei Regen kaum Erholung (nass + warten), trocken etwas.
    if (realSpeed < 1 && st.mode !== 'air') {
      gain += rain > 0.2 ? 0.022 : 0.22;
    }
    if (st.crouching && st.onCarId) gain += 0.08; // kompakt, weniger Angriffsfläche

    const loss = evaporative + night + windChill;
    st.warmth = clamp(st.warmth + (gain - loss) * dt, 0, WARMTH_MAX);

    // Zusammenbrechen vor Kälte — nicht sterben, mode down (Konzept)
    if (st.warmth <= 2 && st.mode !== 'down' && st.mode !== 'hang') {
      this._enterDown();
      if (ctx.log) ctx.log('dir wird schwarz vor Augen');
    }
  }

  _updateAnim(ctx) {
    const st = this.state;
    // Godmode: keine Lauf-Anim an Remotes (wird ohnehin nicht gesendet).
    if (st.mode === 'fly') {
      st.anim = ANIM.IDLE;
      return;
    }
    const sp = Math.hypot(st.velocity.x, st.velocity.z);
    // Auf dem Zug zählt eher die lokale Bewegung
    let moving = sp > 0.4;
    if (st.onCarId && (st.mode === 'ride' || st.mode === 'inside')) {
      // realSpeed steckt in velocity — Bewegung relativ schätzen über runCharge/wish
      moving = this.runCharge > 0.05 || (ctx.input && (() => {
        const a = ctx.input.moveAxis();
        return Math.hypot(a.x, a.y) > 0.1;
      })());
    }

    if (st.mode === 'down') st.anim = ANIM.DOWN;
    else if (st.mode === 'hang' || st.mode === 'mantle') st.anim = ANIM.HANG;
    else if (st.mode === 'climb') st.anim = ANIM.CLIMB;
    else if (!st.grounded && st.mode === 'air') {
      st.anim = st.velocity.y > 0.5 ? ANIM.JUMP : ANIM.FALL;
    } else if (st.crouching) st.anim = ANIM.CROUCH;
    else if (st.mode === 'ride' || st.mode === 'inside') {
      st.anim = st.crouching ? ANIM.RIDE_SIT : (moving ? ANIM.WALK : ANIM.RIDE_SIT);
    } else if (moving) {
      st.anim = this.runCharge > 0.55 ? ANIM.RUN : ANIM.WALK;
    } else {
      st.anim = ANIM.IDLE;
    }
  }

  // --- ctx / Netz -----------------------------------------------------------

  _publish(ctx) {
    const st = this.state;
    ctx.playerState = st;
    ctx.playerPos = st.pos;
    ctx.trackHint = st.trackS;
  }

  _sendNet(ctx) {
    const net = ctx.net;
    if (!net?.sendInput) return;
    const st = this.state;
    // Godmode: nicht übertragen. Ein fliegender Avatar mit Hunderten m/s
    // gehört nicht in das Spiel, das Konzept §8/§10 beschreibt — und würde
    // anderen die Welt als „kaputt" zeigen. Dev-Werkzeug bleibt lokal.
    if (st.mode === 'fly') return;
    net.sendInput({
      x: st.pos.x,
      y: st.pos.y,
      z: st.pos.z,
      yaw: st.yaw,
      pitch: st.pitch,
      anim: st.anim,
      carId: st.onCarId,
      warmth: st.warmth,
      wetness: st.wetness,
    });
  }

  dispose() {
    this.flashlight?.dispose();
    this.flashlight = null;
  }
}

// Für Tests: fester Schrittzugriff / Kalibrierwerte
export const PLAYER_FIXED_DT = FIXED_DT;
export const PLAYER_BALLAST_MUL = BALLAST_SPEED_MUL;
export const PLAYER_ACCEL_RUN_RAMP = ACCEL_RUN_RAMP;
export const PLAYER_COYOTE_TIME = COYOTE_TIME;
export const PLAYER_JUMP_BUFFER = JUMP_BUFFER_TIME;
export const PLAYER_EDGE_LATCH_TTL = EDGE_LATCH_TTL;
export { FLY_SPEED, FLY_SPEED_FAST };
